import {
  CircleAlert,
  HelpCircle,
  ImageOff,
  Mountain,
  MoreHorizontal,
  SpellCheck2,
  type LucideIcon,
} from "lucide-react";

/**
 * Why a learner is reporting a question.
 *
 * Mirrors `ExerciseFlagger::REPORT_REASONS`. The keys are code: they are written
 * into the flag, read back by `/admin/flagged`, and an unknown one is **rejected**
 * server-side rather than filed under „other", because a miscategorised report is
 * worse than none. `spec/services/exercise_flagger_spec.rb` reads this file and
 * fails if the two lists drift — a reason added on one side only is a button that
 * errors when a child presses it.
 *
 * One list, two readers: the child's form (`report-problem.tsx`) and the
 * grown-up's queue (`FlaggedContentPage.tsx`). They used to hold a copy each, so
 * a new reason meant a chip the admin page rendered as „Mokinio pranešimas".
 */
export type ReportReason = {
  /** Code. Never change one — stored flags point at it. */
  key: string;
  /** What the chip says, in the child's voice. */
  label: string;
  icon: LucideIcon;
  /**
   * The question put to the child once this reason is picked. „Gali parašyti
   * plačiau" is a blank page; „Kurį žodį reikėtų pataisyti?" is answerable in
   * four words, and four words is what makes the report actionable.
   */
  prompt: string;
  /**
   * Whether the words are required. Only „Kita" is — it names nothing on its
   * own, so an admin opening it learns exactly what they knew before.
   */
  needsComment?: boolean;
};

export const REPORT_REASONS: readonly ReportReason[] = [
  {
    key: "unclear",
    label: "Nesupratau, ko klausiama",
    icon: HelpCircle,
    prompt: "Kuri vieta buvo neaiški?",
  },
  {
    key: "grammar",
    label: "Yra rašybos ar gramatikos klaida",
    icon: SpellCheck2,
    prompt: "Kurį žodį reikėtų pataisyti?",
  },
  {
    key: "wrong",
    label: "Užduotyje yra klaida",
    icon: CircleAlert,
    prompt: "Kas užduotyje neteisinga?",
  },
  {
    key: "image",
    label: "Nesimato paveikslėlio",
    icon: ImageOff,
    prompt: "Kas matosi toje vietoje, kur turėtų būti paveikslėlis?",
  },
  {
    key: "too_hard",
    label: "Per sunki — dar to nesimokėme",
    icon: Mountain,
    prompt: "Ko tau čia labiausiai neužteko?",
  },
  {
    key: "other",
    label: "Kita",
    icon: MoreHorizontal,
    prompt: "Parašyk, kas negerai",
    needsComment: true,
  },
] as const;

/** Matches `Mutations::ReportExerciseProblem::MAX_COMMENT`. */
export const MAX_REPORT_COMMENT = 500;

export function reportReason(key: string | null | undefined): ReportReason | undefined {
  return REPORT_REASONS.find((r) => r.key === key);
}

/** The chip's words for a stored flag; „—" is never shown, the caller decides. */
export function reportReasonLabel(key: string | null | undefined): string | undefined {
  return reportReason(key)?.label;
}
